/**
 * ReAuthGate Component
 *
 * Re-authentication gate for sensitive operations.
 * Requires password (and MFA if enrolled) within last 10 minutes.
 * Used by both regular users and admin users.
 */

// 1. External dependencies
import React, { useState, useCallback } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

// 2. Internal absolute imports
import {
  Button,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert
} from '@enfomentis/ui';
import { mfaService } from '@/services/MFAService';
import { useAuth } from '@/hooks/useAuth';
import type { MFAErrorCode } from '@/types/mfa';

/**
 * Props for ReAuthGate
 */
export interface ReAuthGateProps {
  children?: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: () => void;
  onReAuth?: () => void;
}

/**
 * Create ephemeral Supabase client for password verification
 *
 * This client is used ONLY for password verification and does NOT:
 * - Persist sessions to localStorage
 * - Emit auth state change events
 * - Auto-refresh tokens
 * - Affect the main application's auth state
 *
 * This prevents unwanted MFA challenges and session churn during re-authentication.
 *
 * @returns Ephemeral Supabase client instance
 */
function createEphemeralClient() {
  const supabaseUrl = import.meta.env['VITE_SUPABASE_URL'];
  const supabaseKey = import.meta.env['VITE_SUPABASE_ANON_KEY'];

  if (!supabaseUrl || !supabaseKey) {
    throw new Error('Missing Supabase environment variables');
  }

  return createClient(supabaseUrl, supabaseKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storage: {
        getItem: async () => null,
        setItem: async () => {},
        removeItem: async () => {},
      },
    },
  });
}

/**
 * Verify password using ephemeral client
 *
 * Creates a temporary Supabase client to verify credentials without
 * affecting the main application's auth state.
 *
 * @param email - User email
 * @param password - User password
 * @throws Error if password verification fails
 */
async function verifyPasswordEphemeral(email: string, password: string): Promise<void> {
  const ephemeralClient = createEphemeralClient();

  const { error } = await ephemeralClient.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw error;
  }

  // IMPORTANT: Do not use ephemeralClient further
  // Do not persist or read its session
  // The session is discarded when this function returns
}

/**
 * Re-Authentication Gate Component
 *
 * Features:
 * - Password re-entry requirement
 * - Validates credentials without updating global auth state (uses ephemeral client)
 * - Updates reauth timestamp on success
 * - 10-minute validity window
 * - Rate limiting protection
 * - No session churn or unwanted MFA challenges
 */
export const ReAuthGate: React.FC<ReAuthGateProps> = ({
  children,
  isOpen = false,
  onOpenChange,
  onReAuth
}) => {
  const { user } = useAuth();

  // Form state
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Toggle password visibility
  const togglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible(prev => !prev);
  }, []);

  // Reset form state
  const resetForm = useCallback(() => {
    setPassword('');
    setError(null);
    setIsSubmitting(false);
    setIsPasswordVisible(false);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    resetForm();
    if (onOpenChange) {
      onOpenChange();
    }
  }, [resetForm, onOpenChange]);

  // Handle re-authentication
  const handleReAuth = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!user?.email) {
      setError('User email not found');
      return;
    }

    if (!password) {
      setError('Please enter your password');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Verify password using ephemeral client
      // CRITICAL: This does NOT create a session in the main app
      // No SIGNED_IN event is fired, no AAL changes, no MFA challenge triggered
      await verifyPasswordEphemeral(user.email, password);

      // Password is correct - update reauth timestamp via Edge Function
      const reauthResult = await mfaService.touchReauth();

      if (!reauthResult.success) {
        // Handle touchReauth errors
        const errorMsg = reauthResult.error.error_code === ('RATE_LIMITED' as MFAErrorCode)
          ? `Too many re-authentication attempts. Try again in ${reauthResult.error.retry_after || 60} seconds.`
          : 'Failed to update authentication status. Please try again.';

        setError(errorMsg);
        setIsSubmitting(false);
        return;
      }

      // Success - execute callback and close modal
      // Now safe to open unenroll modal (no competing MFA modal)
      if (onReAuth) {
        onReAuth();
      }

      handleModalClose();

    } catch (err: unknown) {
      console.error('ReAuthGate error:', err);

      // Map Supabase errors to user-friendly messages
      if (err && typeof err === 'object' && 'message' in err) {
        const errorMessage = (err as { message: string }).message;

        if (errorMessage.toLowerCase().includes('invalid')) {
          setError('Incorrect password. Please try again.');
        } else if (errorMessage.toLowerCase().includes('too many')) {
          setError('Too many attempts. Please try again later.');
        } else {
          setError(errorMessage || 'Authentication failed');
        }
      } else {
        setError('Network error. Please check your connection and try again.');
      }

      setIsSubmitting(false);
    }
  };

  return (
    <>
      {children}

      <Modal
        isOpen={isOpen}
        {...(onOpenChange && { onOpenChange })}
        size="md"
        placement="center"
        backdrop="blur"
        isDismissable={!isSubmitting}
        isKeyboardDismissDisabled={isSubmitting}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h3 className="text-xl font-semibold">Verify Your Identity</h3>
                <p className="text-sm text-text-secondary font-normal">
                  Please re-enter your password to continue
                </p>
              </ModalHeader>

              <ModalBody>
                <form onSubmit={handleReAuth} className="space-y-4">
                  <Alert color="primary" variant="flat">
                    <p className="text-sm">
                      For your security, we need to verify your identity before proceeding with this sensitive action.
                    </p>
                  </Alert>

                  {/* Hidden email field for password manager accessibility */}
                  <input
                    type="email"
                    name="username"
                    autoComplete="username"
                    value={user?.email || ''}
                    readOnly
                    tabIndex={-1}
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      width: '1px',
                      height: '1px',
                      padding: 0,
                      margin: '-1px',
                      overflow: 'hidden',
                      clip: 'rect(0,0,0,0)',
                      whiteSpace: 'nowrap',
                      border: 0
                    }}
                  />

                  {/* Password Input */}
                  <Input
                    type={isPasswordVisible ? "text" : "password"}
                    label="Password"
                    placeholder="Enter your password"
                    value={password}
                    onValueChange={setPassword}
                    isRequired
                    autoComplete="current-password"
                    autoFocus
                    isInvalid={!!error}
                    errorMessage={error || undefined}
                    endContent={
                      <button
                        className="focus:outline-none"
                        type="button"
                        onClick={togglePasswordVisibility}
                        aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                      >
                        {isPasswordVisible ? (
                          <Eye className="w-4 h-4 text-default-400 pointer-events-none" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-default-400 pointer-events-none" />
                        )}
                      </button>
                    }
                    classNames={{
                      input: "text-base", // 16px font size to prevent mobile zoom
                    }}
                  />
                </form>
              </ModalBody>

              <ModalFooter>
                <Button
                  color="default"
                  variant="light"
                  onPress={handleModalClose}
                  isDisabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onPress={() => {
                    const form = document.querySelector('form');
                    if (form) {
                      const event = new Event('submit', { bubbles: true, cancelable: true });
                      form.dispatchEvent(event);
                    }
                  }}
                  isLoading={isSubmitting}
                  isDisabled={!password}
                >
                  {isSubmitting ? 'Verifying...' : 'Verify'}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
